import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, FileText, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { toast } from '@/hooks/use-toast';
import { updateAppointment } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

interface Appointment {
  id: string;
  patient_id: string;
  patientName?: string;
  patientAge?: number;
  patientGender?: string;
  patientPhone?: string;
  date: string;
  time: string;
  reason: string;
  status: string;
}

interface EditAppointmentModalProps {
  appointment: Appointment;
  isOpen: boolean;
  onClose: () => void;
  onUpdated: (updatedAppointment: Appointment) => void;
}

const parseDateForInput = (dateStr: string): string => {
  if (!dateStr) return '';
  return dateStr.split('T')[0];
};

export default function EditAppointmentModal({
  appointment,
  isOpen,
  onClose,
  onUpdated
}: EditAppointmentModalProps) {
  const { currentUser } = useAuth();
  const isStaff = currentUser?.role === 'staff';

  const [isUpdating, setIsUpdating] = useState(false);
  const [statusChangeWarning, setStatusChangeWarning] = useState<string | null>(null);

  const { register, handleSubmit, reset, watch, formState: { isDirty } } = useForm({
    defaultValues: {
      date: parseDateForInput(appointment?.date || ''),
      time: appointment?.time || '',
      reason: appointment?.reason || '',
      status: appointment?.status || 'scheduled'
    }
  });

  // Re-populate form when appointment changes or modal opens
  useEffect(() => {
    if (isOpen && appointment) {
      reset({
        date: parseDateForInput(appointment.date),
        time: appointment.time || '',
        reason: appointment.reason || '',
        status: appointment.status || 'scheduled'
      });
    }
  }, [isOpen, appointment?.id]);

  const watchedStatus = watch('status');

  useEffect(() => {
    if (watchedStatus === appointment?.status) {
      setStatusChangeWarning(null);
      return;
    }
    if (watchedStatus === 'completed' && appointment?.status === 'scheduled') {
      setStatusChangeWarning('This will mark the appointment as completed.');
    } else if (watchedStatus === 'cancelled' && appointment?.status === 'scheduled') {
      setStatusChangeWarning('This will cancel the appointment.');
    } else if (watchedStatus === 'scheduled' && appointment?.status === 'completed') {
      setStatusChangeWarning('Warning: This reverts a completed appointment to scheduled.');
    } else {
      setStatusChangeWarning(null);
    }
  }, [watchedStatus, appointment?.status]);

  const onSubmit = async (data: any) => {
    setIsUpdating(true);
    try {
      const updateData = {
        ...data,
        id: appointment.id,
        patient_id: appointment.patient_id,
        updated_at: new Date().toISOString()
      };
      await updateAppointment(updateData);
      onUpdated(updateData);
      toast({ title: 'Appointment Updated', description: 'Successfully updated' });
      reset();
      onClose();
    } catch {
      toast({ title: 'Update Failed', description: 'Failed to update appointment', variant: 'destructive' });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader className="bg-gradient-to-r from-primary/5 to-primary/10 -m-6 mb-6 p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary/10 rounded-full flex items-center justify-center">
              <Calendar className="w-4 h-4 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-base font-semibold text-foreground">
                Edit Appointment
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">
                Modify appointment details
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Patient info */}
        {appointment?.patientName && (
          <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg mb-2">
            <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
              <User className="w-4 h-4 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground capitalize">{appointment.patientName}</p>
              <p className="text-xs text-muted-foreground">
                {appointment.patientAge}y • {appointment.patientGender} • {appointment.patientPhone}
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">

          {/* Date and Time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">
                Date *
              </label>
              <Input
                type="date"
                {...register('date', { required: true })}
                className="h-9 text-sm"
                disabled={isUpdating}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">
                Time
              </label>
              <Input
                type="time"
                {...register('time')}
                className="h-9 text-sm"
                disabled={isUpdating}
              />
            </div>
          </div>

          {/* Status — doctor only */}
          {!isStaff && (
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">
                Status *
              </label>
              <select
                {...register('status', { required: true })}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none focus:border-ring"
                disabled={isUpdating}
              >
                <option value="scheduled">Scheduled</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              {statusChangeWarning && (
                <div className="mt-2 p-2.5 bg-warning/10 border border-warning/20 rounded-lg flex items-start gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 text-warning mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-foreground">{statusChangeWarning}</p>
                </div>
              )}
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="block text-xs font-medium text-foreground mb-1.5">
              Reason for Visit
            </label>
            <Textarea
              placeholder="Reason for this appointment..."
              {...register('reason')}
              rows={2}
              className="resize-none text-sm"
              disabled={isUpdating}
            />
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <p className="text-xs text-muted-foreground">
              {isDirty ? 'Unsaved changes' : 'No changes'}
            </p>
            <div className="flex items-center gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={isUpdating}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={isUpdating || !isDirty} className="gap-2">
                {isUpdating ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Updating...</>
                ) : (
                  <><CheckCircle className="w-3.5 h-3.5" /> Update</>
                )}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}